/**
 * What the browser has to remember so a refresh does not throw the user out
 * (spec 15.8 - 15.11).
 *
 * The app keeps its whole authenticated context in Redux - signed-in user,
 * store, current location, role permissions, display settings - and Redux is
 * not persisted. On Android that is fine: the store outlives every screen
 * change. In a browser, F5 destroys it, `common.isLoggedIn` comes back false,
 * and App.tsx renders the auth stack.
 *
 * Persisting the whole store would be the wrong fix - it would duplicate
 * authentication state (which 15.10 explicitly rules out) and it would let a
 * stale permission set survive a role change on the server. What this module
 * persists instead is the *context needed to ask the server again*: the store
 * and role the session was bootstrapped with. On startup the app re-runs the
 * very same five calls the PIN screen runs, so the restored session is as
 * fresh as a newly signed-in one, and the server stays the authority on
 * whether the session is still valid.
 *
 * Nothing here is imported by Metro (sessionStore.ts is an empty stand-in), so
 * Android's authentication is untouched.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

import {store} from '../../redux/store';

const SNAPSHOT_KEY = 'zenbasket.pos.session';
const TOKEN_KEY = 'LoginToken';

export type SessionSnapshot = {
  storeId: number;
  roleId: number;
  /** Location the cashier was working at - restored instead of the first one. */
  locationId?: number;
  savedAt: number;
};

/**
 * sessionStorage (per tab) rather than AsyncStorage for the snapshot.
 *
 * AsyncStorage's web build is localStorage underneath, but its API is async -
 * and the snapshot has to be readable *synchronously*, before the first
 * render, so App.tsx can decide whether it is restoring a session or showing
 * the sign-in screen without first painting the wrong one. The token stays on
 * AsyncStorage because that is where the rest of the app already writes it.
 *
 * Only a refresh restores silently (see isPageReload / authBoot.web.tsx):
 * closing the tab or browser and coming back - by any route - asks for the
 * PIN, so a shared counter terminal does not hand the next person the
 * previous cashier's session. sessionStorage keeps the snapshot to this tab;
 * the reload check is still needed because Chrome restores a tab's
 * sessionStorage on "reopen closed tab" and on session restore.
 */
function readRaw(): string | null {
  try {
    return window.sessionStorage.getItem(SNAPSHOT_KEY);
  } catch {
    // Private windows and blocked site data both throw here.
    return null;
  }
}

function writeRaw(value: string | null): void {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(SNAPSHOT_KEY);
    } else {
      window.sessionStorage.setItem(SNAPSHOT_KEY, value);
    }
  } catch {
    /* Storage unavailable - the session simply will not survive a refresh. */
  }
}

/** True when this page load is a browser refresh (F5 / Ctrl+R / reload button). */
export function isPageReload(): boolean {
  try {
    const entry = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (entry) {
      return entry.type === 'reload';
    }
    // Older engines.
    return (performance as any).navigation?.type === 1;
  } catch {
    return false;
  }
}

export function readSessionSnapshot(): SessionSnapshot | null {
  const raw = readRaw();
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<SessionSnapshot>;
    if (typeof parsed?.storeId !== 'number' || typeof parsed?.roleId !== 'number') {
      return null;
    }
    return {
      storeId: parsed.storeId,
      roleId: parsed.roleId,
      locationId: typeof parsed.locationId === 'number' ? parsed.locationId : undefined,
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : 0,
    };
  } catch {
    // Corrupted storage is treated as "no session", not as an error to show
    // the user - 15.23 asks for a clean fall through to sign-in.
    writeRaw(null);
    return null;
  }
}

export function clearSessionSnapshot(): void {
  writeRaw(null);
}

/**
 * The role payload is the only place both identifiers appear together:
 * `roles[0].store_id` and `roles[0].roleid` (components/role/roleModule.ts).
 * Reading them from state rather than from the sign-in response means this
 * works no matter how the session was established.
 */
function snapshotFromState(): SessionSnapshot | null {
  const common = store.getState()?.common;
  if (!common?.isLoggedIn) {
    return null;
  }

  const role = common.posUserRoleSAndPermissions?.value?.roles?.[0];
  const storeId = role?.store_id;
  const roleId = role?.roleid;

  if (typeof storeId !== 'number' || typeof roleId !== 'number') {
    return null;
  }

  // -1 is the slice's "no location yet" placeholder.
  const id = common.currentLocation?.id;
  const locationId = typeof id === 'number' && id > 0 ? id : undefined;

  return {storeId, roleId, locationId, savedAt: Date.now()};
}

/**
 * Mirrors the session context to storage whenever it changes.
 *
 * A plain `store.subscribe` rather than a middleware: the store's middleware
 * chain is shared with Android, and nothing here needs to see actions - the
 * two identifiers are derivable from state alone.
 *
 * Signing out (`loginState(false)`) drops `isLoggedIn`, which makes
 * `snapshotFromState` return null and clears the stored snapshot, so a
 * deliberate sign-out is never resurrected by a refresh.
 */
export function startSessionPersistence(): void {
  // Earlier builds kept the snapshot in localStorage (shared by every tab and
  // surviving a browser close). Remove that leftover copy.
  try {
    window.localStorage.removeItem(SNAPSHOT_KEY);
  } catch {
    /* Storage unavailable - nothing to clean up. */
  }

  let last = '';

  store.subscribe(() => {
    const snapshot = snapshotFromState();
    const next = snapshot
      ? JSON.stringify({
          storeId: snapshot.storeId,
          roleId: snapshot.roleId,
          locationId: snapshot.locationId,
        })
      : '';

    if (next === last) {
      return;
    }
    last = next;
    writeRaw(snapshot ? JSON.stringify(snapshot) : null);
  });
}

/** The bearer token the app stored at sign-in, unwrapped from its JSON. */
export async function readAuthToken(): Promise<string | null> {
  try {
    const raw = await AsyncStorage.getItem(TOKEN_KEY);
    if (!raw) {
      return null;
    }
    /*
     * pin.tsx writes it with JSON.stringify, so the stored value is a quoted
     * string. Older builds wrote it raw; accept both rather than sending a
     * token with quotes around it.
     */
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'string' ? parsed : raw;
    } catch {
      return raw;
    }
  } catch {
    return null;
  }
}

/**
 * Called when the API answers 401 (spec 15.12, 15.23).
 *
 * 401 means the credentials are gone or no longer good, so the context stored
 * for the next refresh is worthless and keeping it would mean re-running a
 * doomed restore on every reload. It is dropped here and nowhere else - no
 * redirect is forced, no other slice is cleared, and the user finishes what
 * they are doing; the next start goes cleanly to sign-in.
 *
 * 403 deliberately does not come here. Forbidden means authenticated but not
 * permitted, which is an authorization answer about one resource, not a
 * statement about the session.
 */
export function onUnauthorized(): void {
  clearSessionSnapshot();
}
